import { Viewer } from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import {
  SearchRuleEntry,
  SearchToolSnapshot,
  SearchRuleStyle,
  getStyleForRuleStyle,
} from "./AssetsPropertiesSearchToolPlugin";
import TilesetStylingPlugin, {
  StyleRuleIdentifier,
  TilesetStylePriority,
} from "./TilesetStylingPlugin";
import { Search, FilterNode } from "lib/cesium/search";

export type LayerRuleSet = {
  layerId: number;
  tilesetIds: string[];
  rules: SearchRuleEntry[];
};

const LAYER_CRITERIA_PRIORITY = TilesetStylePriority.ElementSearch;

/**
 * Manages per-layer rule styling.
 * This is not a serializable tool; layer rules are stored on the Layer
 * record and applied automatically for visible layers.
 */
export default class AssetsPropertiesLayerCriteriaPlugin
  implements AttachablePluginInterface
{
  private readonly _activeRuleIdentifiers: StyleRuleIdentifier[] = [];
  private readonly _layerRuleSets = new Map<number, LayerRuleSet>();
  private readonly _layerDrafts = new Map<number, SearchToolSnapshot>();

  constructor(private readonly _tilesetStylingPlugin: TilesetStylingPlugin) {}

  setViewer(_viewer: Viewer): void {
    //no viewer reference is needed
  }

  detachViewer(): void {
    this._activeRuleIdentifiers.length = 0;
  }

  setRuleSets(ruleSets: LayerRuleSet[]) {
    this._layerRuleSets.clear();
    ruleSets.forEach((ruleSet) =>
      this._layerRuleSets.set(ruleSet.layerId, ruleSet),
    );
    this.syncRulesAndApply();
  }

  setLayerDraft(layerId: number, snapshot: SearchToolSnapshot | null) {
    if (!layerId) return;
    if (!snapshot) this._layerDrafts.delete(layerId);
    else this._layerDrafts.set(layerId, snapshot);
    this.syncRulesAndApply();
  }

  private syncRulesAndApply() {
    this.removeActiveRules();
    this.buildRules();
    void this._tilesetStylingPlugin.applyRules();
  }

  private removeActiveRules() {
    this._activeRuleIdentifiers.forEach((rule) =>
      this._tilesetStylingPlugin.removeRule(rule),
    );
    this._activeRuleIdentifiers.length = 0;
  }

  private buildRules() {
    const ruleSetsOrdered = Array.from(this._layerRuleSets.values())
      .map((ruleSet) => ({
        ...ruleSet,
        rules:
          this._layerDrafts.get(ruleSet.layerId)?.searches ??
          ruleSet.rules ??
          [],
      }))
      .toSorted((a, b) => a.layerId - b.layerId);

    const uniqueTilesetIdsByLayer = new Map<number, string[]>(
      ruleSetsOrdered.map((ruleSet) => [
        ruleSet.layerId,
        Array.from(new Set(ruleSet.tilesetIds ?? [])).sort(),
      ]),
    );

    const rankedRules = ruleSetsOrdered.flatMap((ruleSet) =>
      (ruleSet.rules ?? [])
        .filter((r) => r.enabled && Object.keys(r.facets ?? {}).length > 0)
        .toSorted((a, b) => (a.position < b.position ? -1 : 1))
        .map((rule) => ({
          rule,
          tilesetIds: uniqueTilesetIdsByLayer.get(ruleSet.layerId) ?? [],
        })),
    );

    rankedRules.forEach(({ rule, tilesetIds }, rank) => {
      const criteriaNode = Search.fromFacets(rule.facets);
      if (!criteriaNode) return;
      tilesetIds.forEach((tilesetId, tilesetIndex) => {
        const priorityOffset = rank / 256 + tilesetIndex / 65536;
        const priority = LAYER_CRITERIA_PRIORITY + priorityOffset;
        this._tilesetStylingPlugin.setRule(
          this.createRule(criteriaNode, rule.style, priority, tilesetId),
        );
        this._activeRuleIdentifiers.push({
          priority,
          conditionStyle: { tilesetId },
        });
      });
    });
  }

  private createRule(
    criteria: FilterNode,
    style: SearchRuleStyle,
    priority: number,
    tilesetId: string,
  ) {
    const condition = Search.toCesium3dTileStyleCondition(criteria);
    return {
      priority,
      conditionStyle: {
        tilesetId,
        condition,
        ...getStyleForRuleStyle(style),
      },
    };
  }
}
